import * as React from 'react';
import {
  root as rootCls,
  iconSlot as iconSlotCls,
  title as titleCls,
  description as descCls,
  actions as actionsCls,
} from './EmptyState.css';

export interface EmptyStateProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Optional decorative icon / illustration. */
  icon?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Primary action — typically a Button. */
  action?: React.ReactNode;
  /** Secondary action — typically a ghost Button or link. */
  secondaryAction?: React.ReactNode;
  /** React 19 ref-as-prop. */
  ref?: React.Ref<HTMLDivElement>;
}

/**
 * EmptyState — centred "no content" / "first-time-here" surface.
 * Composition: optional icon + heading + description + one or two
 * action slots. Sets `role="status"` so screen readers announce the
 * state when it appears (e.g. on a fresh tenant first login).
 *
 * Validated against Daniel/Sophie tenant first-login screens in the
 * prototype.
 */
export function EmptyState({
  icon,
  title,
  description,
  action,
  secondaryAction,
  ref,
  className,
  ...rest
}: EmptyStateProps) {
  const cls = [rootCls, className].filter(Boolean).join(' ');
  return (
    <div ref={ref} className={cls} role="status" {...rest}>
      {icon && (
        <div className={iconSlotCls} aria-hidden="true">
          {icon}
        </div>
      )}
      <h3 className={titleCls}>{title}</h3>
      {description && <p className={descCls}>{description}</p>}
      {(action || secondaryAction) && (
        <div className={actionsCls}>
          {action}
          {secondaryAction}
        </div>
      )}
    </div>
  );
}
EmptyState.displayName = 'EmptyState';
